<template>
  <div :class="$style.wrapper">
    <div :class="[$style.map, 'map']" ref="map"></div>
    <buttons @zoomIn="zoomIn" @zoomOut="zoomOut" @centroid="centroid"/>
  </div>
</template>
<script>
/* eslint-disable no-unused-vars */

import { TimelineMax, TweenMax } from 'gsap'

import load from '@/services/map-api'
import bearing from '@/utils/bearing'


import {
  getLinestringForwardTween,
  getPlacemarkPositionTween,
  getPlacemarkRotationTween,
  POSITION_MULTI,
} from '@/utils/mapTweenUtils'

import buttons from './buttons/buttons.vue'

const getDelay = (value) => {
  const proxyValue = { x: 0 }
  return TweenMax.to(proxyValue, { x: 10, duration: value })
}

export default {
  name: 'local-map',
  components: { buttons },
  props: {
    center: {
      type: Array,
      default: () => [53.188195, 50.122227],
    },
    zoom: {
      type: Number,
      default: 16,
    },
    zoomScale: {
      type: Number,
      default: 16,
    },
  },
  watch: {
    center(center) {
      this.setCenter(center)
    },
  },
  async mounted() {
    this.maps = await load()

    const { maps, center, zoom } = this

    this.map = new maps.Map(this.$refs.map, {
      center,
      zoom,
      controls: [],
    }, {
      suppressMapOpenBlock: true,
      yandexMapDisablePoiInteractivity: true,
    })

    this.map.events.add('boundschange', (event) => {
      const newZoom = event.get('newZoom')
      this.resizeGeoObjects(newZoom)
    })

    this.map.geoObjects.events.add('add', () => {
      this.resizeGeoObjects(this.zoom)
    })

    if (process.env.NODE_ENV !== 'production') {
      this.map.events.add('click', e => console.log(e.get('coords')))
    }

    this.$emit('apiReady', this.maps)
  },
  methods: {
    getYaApi() {
      return this.maps
    },
    resizeGeoObjects(zoom) {
      const { geoObjects } = this.map
      for (let i = 0; i < geoObjects.getLength(); i += 1) {
        // eslint-disable-next-line no-restricted-properties
        geoObjects.get(i).properties.set('scale', Math.pow(zoom / this.zoomScale, 4))
      }
    },
    setCenter(center) {
      this.map.setCenter(center, this.zoom, { duration: 400 })
    },
    getAnimation({
      placemark, path, speedMultiply = 1, noRotation = false, delay = null,
    }) {
      const animation = new TimelineMax({ paused: true })

      if (delay) {
        animation.add(getDelay(delay))
      }

      if (path && path.length > 0) {
        const [point] = path
        const coords = { x: point[0] * POSITION_MULTI, y: point[1] * POSITION_MULTI }

        if (path.length > 1) {
          placemark.properties.set('rotation', bearing(path[0], path[1]))
        }

        let labelIndex = 0

        path.forEach((item, index) => {
          animation.add(getPlacemarkPositionTween(placemark, coords, path, index, speedMultiply))
          animation.addLabel(`step${labelIndex}`)
          labelIndex += 1

          if (index !== path.length - 1 && !noRotation) {
            const prev = index > 0 ? bearing(path[index - 1], path[index]) : undefined
            animation.add(getPlacemarkRotationTween(placemark, bearing(path[index], path[index + 1]), prev))
            animation.addLabel(`step${labelIndex}`)
            labelIndex += 1
          }
        })
      }

      return animation
    },
    updateMovingMarkerPath({ placemark, path, speedMultiply = 1 }) {
      const animation = this.getAnimation({ placemark, path, speedMultiply })
      placemark.geometry.setCoordinates(path[0])
      return {
        placemark,
        animation,
      }
    },
    addMovingLineString({ path, object, speedMultiply = 1 }) {
      const animation = new TimelineMax({ paused: true })

      for (let i = 0; i < path.length - 1; i += 1) {
        const linestringTween = getLinestringForwardTween(object.geometry, path, i, speedMultiply)
        animation.add(linestringTween)
      }

      this.addOnMap(object)

      return {
        line: object,
        animation,
      }
    },
    addMovingPlacemark({
      layout, point, path, speedMultiply = 1, noRotation = false, delay = null,
    }) {
      const { placemark } = this.addStaticPlacemark({ layout, point, noRotation })

      const animation = this.getAnimation({
        placemark, path, speedMultiply, noRotation, delay,
      })

      return {
        placemark,
        animation,
      }
    },
    addStaticPlacemark({
      layout, point,
    }) {
      const { map, maps } = this
      const template = maps.templateLayoutFactory.createClass(layout)
      const placemark = new maps.Placemark(
        point,
        { rotation: 0, scale: 1 },
        {
          iconLayout: template,
          iconShape: {
            type: 'Point',
            // iconOffset: [-30, -30]
            coordinates: [60, 60],
          },
        },
      )
      map.geoObjects.add(placemark)

      return {
        placemark,
      }
    },
    addOnMap(item) {
      this.map.geoObjects.add(item)
      return item
    },
    removeFromMap(item) {
      this.map.geoObjects.remove(item)
    },
    zoomIn() {
      const { map } = this
      const zoom = map.getZoom()
      map.setZoom(zoom + 1, { duration: 100 })
    },
    zoomOut() {
      const { map } = this
      const zoom = map.getZoom()
      map.setZoom(zoom - 1, { duration: 100 })
    },
    centroid() {
      const { map, center, zoom } = this
      map.setCenter(center, zoom, { duration: 100 })
    },
  },
}
</script>
<style lang="scss" module>
.wrapper {
  position: relative;
  height: 100%;

  :global(ymaps[class$="-places-pane"]) {
    z-index: 9999999 !important;
  }
}

.map {
  width: 100%;
  height: 100%;
}
</style>
